
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';
import { getLanguageName } from '@/utils/translationHelper';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ar', name: 'العربية' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'ku', name: 'Kurdî' }
];

interface LanguageSwitcherProps {
  variant?: 'default' | 'minimal' | 'mobile';
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'default', className = '' }) => {
  const { i18n, t } = useTranslation();
  
  const changeLanguage = useCallback((lng: string) => {
    i18n.changeLanguage(lng);
    // Save selected language to localStorage
    localStorage.setItem('i18nextLng', lng);
  }, [i18n]);

  // Get current language name
  const getCurrentLanguageName = useCallback(() => {
    return getLanguageName(i18n.language);
  }, [i18n.language]);

  if (variant === 'minimal') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className={className}>
            <Globe className="h-5 w-5" />
            <span className="sr-only">{t('common.language')}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {languages.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`${i18n.language === lang.code ? 'bg-accent' : ''} cursor-pointer`}
            >
              {lang.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className={`gap-y-2 ${className}`}>
        <h3 className="text-sm font-medium text-muted-foreground mb-3">{t('common.language')}</h3>
        <div className="grid grid-cols-2 gap-2">
          {languages.map((lang) => (
            <Button
              key={lang.code}
              variant={i18n.language === lang.code ? "default" : "outline"}
              size="sm"
              onClick={() => changeLanguage(lang.code)}
              className={`justify-center ${i18n.language === lang.code ? 'bg-brand-yellow text-brand-black hover:bg-brand-yellow/90' : ''}`}
            >
              {lang.name}
            </Button>
          ))}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={`flex items-center gap-2 ${className}`}>
          <Globe className="h-4 w-4" />
          <span>{getCurrentLanguageName()}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={`${i18n.language === lang.code ? 'bg-accent' : ''} cursor-pointer`}
          >
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
